import React, { useEffect, useRef, type ComponentProps } from 'react';
import type { Control } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { DynamicFormRequiredType, HfInput } from '@myosh/odin-components';
import type { DropDownResult, DynamicFieldDataTypes } from '@myosh/odin-components';
import HfDropDown from '../fields/custom-drop-downs/hf-drop-down.component';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { DEFAULT_FIELD_TYPE_OPTION, sortedFieldTypeDropdownOptions } from './util/field-details.util';
import { forceAssert } from '../../utils/type-guards';
import i18next from '../../i18n';

interface InlineAddFieldPlaceholderFormValues {
  name: string;
  type: DropDownResult;
}

const schema: yup.ObjectSchema<InlineAddFieldPlaceholderFormValues> = yup.object({
  name: yup
    .string()
    .trim()
    .required(i18next.t('required-field', { label: i18next.t('name') })),
  // DropDownResult has extra fields we don't care about here.
  type: yup
    .mixed<DropDownResult>()
    .required()
    .test('has-value-and-text', 'Type required', (v) => {
      return Boolean(v.value) && Boolean(v.text);
    }),
});

export interface InlineAddFieldPlaceholderCommitPayload {
  name: string;
  type: DropDownResult;
}

interface InlineAddFieldPlaceholderProps {
  /**
   * Called when the placeholder successfully validates and commits.
   */
  onCommit: (payload: InlineAddFieldPlaceholderCommitPayload) => void;

  /**
   * Called when the user cancels (Esc).
   */
  onCancel: () => void;

  /**
   * Called when the user clicks outside the placeholder.
   * If omitted, the component will still auto-commit on outside click.
   */
  onOutsideClick?: () => void;
}

export const InlineAddFieldPlaceholder = ({
  onCommit,
  onCancel,
  onOutsideClick,
  className,
  ...props
}: InlineAddFieldPlaceholderProps & ComponentProps<'div'>) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InlineAddFieldPlaceholderFormValues>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      name: '',
      type: DEFAULT_FIELD_TYPE_OPTION,
    },
  });

  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    nameInputRef.current?.focus();
  }, []);

  const submit = handleSubmit((data) => {
    onCommit(data);
  });

  useEffect(() => {
    const handleDocumentMouseDown = async (event: MouseEvent) => {
      const el = placeholderRef.current;
      if (!el) return;

      const path = event.composedPath();
      if (path.includes(el)) return;

      onOutsideClick?.();

      submit();
    };

    document.addEventListener('mousedown', handleDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [onOutsideClick, submit]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
        reset();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onCancel, reset]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      ref={placeholderRef}
      className={clsx('flex items-start gap-3 rounded border border-gray-4 bg-mono-1 p-3', className)}
      onKeyDown={handleKeyDown}
      {...props}
    >
      <div className="flex flex-1 flex-col gap-2">
        <HfInput
          control={forceAssert<Control>(control)}
          name="name"
          label={t('name')}
          enableDebounce={false}
          required={DynamicFormRequiredType.True}
          readOnly={false}
          inputRef={(el) => {
            nameInputRef.current = el as HTMLInputElement | null;
          }}
          error={errors.name?.message}
        />
        <HfDropDown
          control={forceAssert<Control>(control)}
          name="type"
          label={t('type')}
          allowClear={false}
          allowSearch={true}
          textField="text"
          valueField="value"
          data={forceAssert<DynamicFieldDataTypes>(sortedFieldTypeDropdownOptions)}
          value={DEFAULT_FIELD_TYPE_OPTION}
          required={DynamicFormRequiredType.True}
        />
        <div className="text-xs text-gray-3">
          Press <span className="font-medium">Enter</span> to add, <span className="font-medium">Esc</span> to cancel
        </div>
      </div>
    </div>
  );
};
